'use client';

import { Icon } from '@/components/ui/Icon';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useModal } from '@/hooks/useModal';
import { normalizeCmsPopupId } from '@/lib/utils/normalizeCmsPopupId';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';

import styles from './Popup.module.scss';
import { PopupContentContext } from './PopupContentContext';
import { getAnimationClasses, getCloseDuration, isDarkBackground } from './Popup.utils';
import { popupEntries } from './PopupRegistry';

/**
 * Single popup renderer — placed once in the layout.
 * Runs useModal, useFocusTrap, and escape key listener a single time.
 * Content is resolved from PopupRegistry via the active popupId.
 */
export const Popup: FC = () => {
  const t = useTranslations('common');
  const { isOpen, data, close } = useModal('cms-popup');

  // Re-render when the registry updates so a newly registered popup id can resolve.
  useSyncExternalStore(popupEntries.subscribe, popupEntries.getRegistryVersion, () => 0);

  const [isClosing, setIsClosing] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const closingRef = useRef(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pathname = usePathname();
  const pathnameRef = useRef(pathname);

  useEffect(() => {
    if (pathnameRef.current !== pathname && isOpen) {
      close();
    }
    pathnameRef.current = pathname;
  }, [pathname, isOpen, close]);

  const resolvedPopupId = normalizeCmsPopupId(data?.popupId);
  const isActive = isOpen && resolvedPopupId.length > 0;

  const focusTrapRef = useFocusTrap<HTMLDivElement>(isActive && !isClosing);

  const handleClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    setIsClosing(true);

    closeTimerRef.current = setTimeout(() => {
      close();
      setIsClosing(false);
      closingRef.current = false;
      closeTimerRef.current = null;
    }, getCloseDuration());
  }, [close]);

  useEffect(() => {
    if (!isOpen) {
      closingRef.current = false;
      setIsClosing(false);
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!resolvedPopupId) return;
    closingRef.current = false;
    setIsClosing(false);
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, [resolvedPopupId]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === overlayRef.current) {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose]
  );

  const popupContextValue = useMemo(
    () => ({ isInsidePopup: true, scrollRef: scrollContainerRef }),
    []
  );

  const containerRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      (scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      (focusTrapRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [focusTrapRef]
  );

  if (!isActive || !resolvedPopupId) return null;

  const entry = popupEntries.getEntry(resolvedPopupId);
  if (!entry) {
    return null;
  }

  const effectiveAnimation = data?.animation ?? entry.animation;
  const effectiveMaxWidth = data?.maxWidth ?? entry.maxWidth ?? 'md';
  const popupBackgroundColor = data?.backgroundColor ?? entry.backgroundColor;

  const isFull = effectiveMaxWidth === 'full';
  const isDark = isDarkBackground(popupBackgroundColor);
  const animationClass = getAnimationClasses(effectiveAnimation, isClosing);

  return createPortal(
    <div
      ref={overlayRef}
      role="presentation"
      className={clsx(styles.overlay, !isFull && styles.overlayBlur)}
      onClick={handleOverlayClick}
      data-testid="popup"
    >
      <div
        ref={containerRefCallback}
        className={clsx(styles.container, styles[`maxWidth-${effectiveMaxWidth}`], animationClass)}
        role="dialog"
        aria-modal="true"
        aria-labelledby="popup-title"
        style={popupBackgroundColor ? { backgroundColor: popupBackgroundColor } : undefined}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div
          id="popup-title"
          className={styles.closeBar}
          style={popupBackgroundColor ? { background: popupBackgroundColor } : undefined}
        >
          <button
            type="button"
            className={clsx(styles.closeButton, isDark && styles.closeButtonLight)}
            onClick={handleClose}
            aria-label={t('close')}
            data-testid="popup-close"
          >
            <Icon name="close" size={20} />
          </button>
        </div>
        <div className={styles.content}>
          <PopupContentContext.Provider value={popupContextValue}>
            {entry.children}
          </PopupContentContext.Provider>
        </div>
      </div>
    </div>,
    document.body
  );
};
